// src/pages/Attendance.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Checkbox, 
  CircularProgress,
  FormControlLabel, 
  FormGroup, 
  Paper, 
  TextField, 
  Typography 
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';

const STAFF_API_URL = "https://htm.bayex.lk/api/get_staff.php";
// Ensure that your backend attendance_management.php accepts an "attendance_date" parameter
const ATTENDANCE_API_URL = "https://htm.bayex.lk/api/attendance_management.php";

const Attendance = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for the selected attendance date.
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  // State for the list of staff.
  const [staffList, setStaffList] = useState([]);
  // State for attendance mapping: key = staff id, value = true (attended) or false (absent).
  const [attendance, setAttendance] = useState({});
  // State for feedback message.
  const [message, setMessage] = useState("");
  // New state for loading spinner when fetching attendance.
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // Fetch the staff list from the backend.
  useEffect(() => {
    fetch(STAFF_API_URL)
      .then(res => res.json())
      .then(data => {
        setStaffList(data);
        // Initialize attendance state for all staff to false.
        const initAttendance = {};
        data.forEach(staff => {
          initAttendance[staff.id] = false;
        });
        setAttendance(initAttendance);
      })
      .catch(err => console.error("Error fetching staff list:", err));
  }, []);

  // When attendanceDate (or staffList) changes, fetch attendance records for that date.
  useEffect(() => {
    // Only attempt to fetch if we already have the staff list.
    if (staffList.length === 0) return;
    
    setLoadingAttendance(true);
    fetch(`${ATTENDANCE_API_URL}?attendance_date=${attendanceDate}`)
      .then(res => res.json())
      .then(data => {
        // Build a new attendance object: initialize all to false, then override with fetched records.
        const updatedAttendance = {};
        staffList.forEach(staff => {
          updatedAttendance[staff.id] = false;
        });
        // Assume that each fetched record has: staff_id and attended (1 for attended, 0 for absent).
        data.forEach(record => {
          updatedAttendance[record.staff_id] = record.attended == 1;
        });
        setAttendance(updatedAttendance);
        setLoadingAttendance(false);
      })
      .catch(err => {
        console.error("Error fetching attendance for date:", err);
        setLoadingAttendance(false);
      });
  }, [attendanceDate, staffList]);

  // Handler for checkbox change.
  const handleCheckboxChange = (event, staffId) => {
    setAttendance(prev => ({
      ...prev,
      [staffId]: event.target.checked,
    }));
  };

  // Submit the attendance record.
  const handleSubmitAttendance = () => {
    const payload = {
      action: "submit_attendance",
      date: attendanceDate,
      attendance: attendance,
    };

    fetch(ATTENDANCE_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(data => {
        setMessage(data.message || "Attendance submitted successfully.");
      })
      .catch(err => {
        console.error("Error submitting attendance:", err);
        setMessage("Error submitting attendance.");
      });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Attendance
      </Typography>
      <TextField 
        label="Attendance Date" 
        type="date" 
        value={attendanceDate} 
        onChange={(e) => setAttendanceDate(e.target.value)} 
        InputLabelProps={{ shrink: true }} 
        sx={{ mb: 2 }} 
      />
      {/* Show spinner while fetching attendance */}
      {loadingAttendance ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormGroup>
            {staffList.map(staff => (
              <FormControlLabel
                key={staff.id}
                control={
                  <Checkbox
                    checked={attendance[staff.id] || false}
                    onChange={(e) => handleCheckboxChange(e, staff.id)}
                  />
                }
                label={`${staff.name} (${staff.id}${staff.phone ? `, ${staff.phone}` : ""})`}
              />
            ))}
          </FormGroup>
        </Paper>
      )}
      <Button variant="contained" onClick={handleSubmitAttendance}>
        Submit Attendance
      </Button>
      {message && (
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default Attendance;
